import { useState } from 'react';
import { Link } from 'react-router-dom';
import CreateDistrict from '../create-district';
import CheckboxToggler from '../../checkbox-toggler';
import Takeover from '../../takeover';
import { Button } from '../../forms';
import SpinnerDots from '../../spinner/dots';
import Skeleton from '../../skeleton/Skeleton';
import './panel-report.scss';
import classNames from 'classnames';
import {
    formatDistrictAreaKm2,
    formatDistrictTurnover,
    formatDistrictFrequencyAndVisitTime
} from './district-data-format';

const PanelReport = ({ allDistricts, type, match, handlers, isLoading = false }) => {
    const scenarioId = match?.params?.scenarioId || null;
    const [deleteDialogState, setDeleteDialogState] = useState({ isOpen: false, district: null, isDeleting: false });

    function openDeleteDialog(district) {
        setDeleteDialogState({ isOpen: true, district, isDeleting: false });
    }

    function closeDeleteDialog() {
        setDeleteDialogState({ isOpen: false, district: null, isDeleting: false });
    }

    async function handleDeleteDistrict(districtId) {
        setDeleteDialogState(prev => ({ ...prev, isDeleting: true }));
        try {
            await handlers.deleteDistrict(districtId);
            closeDeleteDialog();
        } catch (error) {
            setDeleteDialogState(prev => ({ ...prev, isDeleting: false }));
        }
    }

    return (
        <div>
            {isLoading && allDistricts.length === 0 && (
                <div className="panel-report">
                    <header className="panel-report__header">
                        <h2 className="panel-report__title">Alla distrikt</h2>
                    </header>
                    {[0, 1].map(i => (
                        <div className="panel-report__district" key={`skeleton-${i}`}>
                            <header className="panel-report__district-header">
                                <div className="panel-report__district-link">
                                    <Skeleton variant="circular" width="16px" height="16px" />
                                    <Skeleton width="100px" height="16px" />
                                </div>
                            </header>
                            <div className="panel-report__district-content">
                                <ul className="panel-report__district-data">
                                    <li>
                                        <h3 className="panel-report__district-data-heading">Distriktsarea</h3>
                                        <span className="panel-report__district-data-value"><Skeleton width="70px" /></span>
                                    </li>
                                    <li>
                                        <h3 className="panel-report__district-data-heading">Enheter</h3>
                                        <span className="panel-report__district-data-value"><Skeleton width="30px" /></span>
                                    </li>
                                    <li>
                                        <h3 className="panel-report__district-data-heading">Omsättning</h3>
                                        <span className="panel-report__district-data-value"><Skeleton width="80px" /></span>
                                    </li>
                                    {type !== 'foodservice' && (
                                    <li>
                                        <h3 className="panel-report__district-data-heading">Frekvens / Besökstid</h3>
                                        <span className="panel-report__district-data-value"><Skeleton width="80px" /></span>
                                    </li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {allDistricts.length > 0 && (
                <div className="panel-report">
                    <header className="panel-report__header">   
                        <h2 className="panel-report__title">Alla distrikt</h2>
                        <CreateDistrict
                            type={type}
                            scenarioId={scenarioId}
                            allDistricts={allDistricts}
                            saveSingleDistrict={(district) => handlers.saveSingleDistrict(district)}
                        />
                    </header>
                    {allDistricts.map(district => {
                        return (
                            <div className={classNames({
                                'panel-report__district': true,
                                'panel-report__district--active': district.id === parseInt(match.params.id, 10)
                            })} key={district.id}>
                                <header className="panel-report__district-header">
                                    <Link
                                        className="panel-report__district-link"
                                        to={`${type === 'foodservice' ? `/foodservice-scenario` : `/scenario`}/${district.salesDistrictScenarioId}/create/${district.id || ''}`}>
                                        <span className="panel-report__district-color" style={{ backgroundColor: district.color }}></span>
                                        <h3 className="panel-report__district-title">{district.name}</h3>
                                    </Link>
                                    <CheckboxToggler
                                        checked={district.visible}
                                        disabled={district.id === parseInt(match.params.id, 10)}
                                        onChange={(e) => {
                                            handlers.toggleSaleDistrict(district.id, e.target.checked);
                                        }}
                                    />
                                    <div className="panel-report__district-actions">
                                        <button
                                            type="button"
                                            onClick={() => openDeleteDialog(district)}
                                            className="panel-report__district-delete-button"
                                        >
                                            <span>Radera</span>
                                        </button>
                                    </div>
                                </header>
                                <div className="panel-report__district-content">
                                    <ul className="panel-report__district-data">
                                        <li>
                                            <h3 className="panel-report__district-data-heading">Distriktsarea</h3>
                                            <span className="panel-report__district-data-value">{formatDistrictAreaKm2(district.salesDistrictArea)}</span>
                                        </li>
                                        <li>
                                            <h3 className="panel-report__district-data-heading">Enheter</h3>
                                            <span className="panel-report__district-data-value">{district.numberOfHits || 0}</span>
                                        </li>
                                        <li>
                                            <h3 className="panel-report__district-data-heading">Omsättning</h3>
                                            <span className="panel-report__district-data-value">{formatDistrictTurnover(district.turnOver)}</span>
                                        </li>
                                        {type !== 'foodservice' && (
                                        <li>
                                            <h3 className="panel-report__district-data-heading">Frekvens / Besökstid</h3>
                                            <span className="panel-report__district-data-value">{formatDistrictFrequencyAndVisitTime(district.frequency, district.vistingTimeHours)}</span>
                                        </li>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <Takeover
                isOpen={deleteDialogState.isOpen}
                toggleTakeover={closeDeleteDialog}
                heading="TA BORT DISTRIKT"
            >
                <div style={{ padding: '20px' }}>
                    <p>Vill du ta bort distriktet permanent?</p>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                        <Button
                            type="button"
                            modifiers={{ 'districts__takeover-controls-btn--danger': true }}
                            onClick={() => handleDeleteDistrict(deleteDialogState.district?.id)}
                            disabled={deleteDialogState.isDeleting}
                        >
                            {deleteDialogState.isDeleting ? 'Raderar...' : 'Ta bort distrikt'}
                        </Button>
                        <Button
                            type="button"
                            modifiers={{ 'districts__takeover-controls-btn--outline': true }}
                            onClick={closeDeleteDialog}
                            disabled={deleteDialogState.isDeleting}
                        >
                            Avbryt
                        </Button>
                    </div>
                    {deleteDialogState.isDeleting && <SpinnerDots />}
                </div>
            </Takeover>
        </div>
    );
};
/*
<CheckboxToggler
    checked={allDistricts.every(d => d.visible)}
    onChange={(e) => {
        handlers.toggleAllSaleDistricts(e.target.checked);
    }}
/>
*/
export default PanelReport;
